import React, { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  X,
  History,
  Package,
  Tag,
  Info
} from 'lucide-react';
import InventoryHistoryComponent from './InventoryHistoryComponent';

/**
 * Modal dialog for displaying inventory history
 * Wraps InventoryHistoryComponent in a responsive dialog
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Close dialog callback
 * @param {string} props.productId - Product ID for history lookup
 * @param {Object} props.productInfo - Product information object
 * @param {boolean} props.fullScreen - Force full screen mode
 * @param {number} props.maxWidth - Maximum dialog width
 */
const InventoryHistoryDialog = React.memo(({
  open,
  onClose,
  productId,
  productInfo = null,
  fullScreen = false,
  maxWidth = 'lg',
  selectedClientId = 'global'
}) => {
  // Check for mobile using window width
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 640;

  // Force full screen on small screens or when explicitly set
  const shouldUseFullScreen = fullScreen || isSmallScreen;

  // Determine effective client ID - use product's client_id if available (for global view),
  // otherwise use selectedClientId
  const effectiveClientId = productInfo?.client_id || selectedClientId;

  // Handle dialog close
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Product info from either productInfo prop or derive from productId
  const displayProductInfo = {
    name: productInfo?.name || '产品',
    product_id: productInfo?.product_id || productId,
    category: productInfo?.category,
    part_number: productInfo?.part_number,
    remaining_quantity: (() => {
      if (!productInfo) return undefined;
      if (!effectiveClientId || effectiveClientId === 'global') return productInfo.remaining_quantity;
      if (productInfo.client_inventory?.[effectiveClientId]) {
        return productInfo.client_inventory[effectiveClientId].remaining_quantity;
      }
      if (typeof productInfo.client_quantity !== 'undefined' && productInfo.client_quantity !== null) {
        return productInfo.client_quantity;
      }
      return productInfo.remaining_quantity;
    })(),
    ...(productInfo || {})
  };

  // Render product info header
  const renderProductHeader = () => (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <span className="text-lg font-semibold truncate">
            {displayProductInfo.name}
          </span>
        </div>

        {displayProductInfo.product_id && (
          <Badge variant="outline" className="flex items-center gap-1">
            <Tag className="h-3 w-3" />
            {displayProductInfo.product_id}
          </Badge>
        )}

        {displayProductInfo.category && (
          <Badge variant="secondary">
            {displayProductInfo.category}
          </Badge>
        )}

        {displayProductInfo.remaining_quantity !== undefined && (
          <Badge className="flex items-center gap-1">
            <Info className="h-3 w-3" />
            库存: {displayProductInfo.remaining_quantity || 0}
          </Badge>
        )}
      </div>

      {displayProductInfo.part_number && (
        <p className="text-sm text-muted-foreground mt-2">
          零件号: {displayProductInfo.part_number}
        </p>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent
        className={`
          ${shouldUseFullScreen ? 'w-screen h-screen max-w-none m-0 rounded-none' : 'max-w-4xl h-[90vh]'}
          flex flex-col overflow-hidden
        `}
      >
        {/* Dialog Title */}
        <DialogHeader className="pb-4 border-b bg-muted/50 -mx-6 -mt-6 px-6 pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              <DialogTitle>库存历史记录</DialogTitle>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              aria-label="关闭对话框"
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Dialog Content */}
        <div className="flex-1 overflow-hidden flex flex-col pt-4">
          {/* Product Info Header */}
          {renderProductHeader()}

          <Separator className="my-4" />

          {/* History Component */}
          <div className="flex-1 overflow-auto min-h-0">
            <InventoryHistoryComponent
              productId={productId}
              productInfo={displayProductInfo}
              compact={shouldUseFullScreen}
              maxHeight={shouldUseFullScreen ? '100%' : 500}
              selectedClientId={effectiveClientId}
            />
          </div>
        </div>

        {/* Dialog Actions - only show on mobile or if needed */}
        {shouldUseFullScreen && (
          <DialogFooter className="border-t bg-muted/50 -mx-6 -mb-6 px-6 py-4">
            <Button
              onClick={handleClose}
              className={isMobile ? 'w-full' : ''}
              size={isMobile ? 'lg' : 'default'}
            >
              关闭
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
});

InventoryHistoryDialog.displayName = 'InventoryHistoryDialog';

export default InventoryHistoryDialog;
